import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { MediaUpload } from '@/components/common/MediaUpload';
import { ResponsiveCombobox, type ComboboxItem } from '@/components/ui/responsive-combobox';
import { useOrganizations, useCreateOrganization } from '@/features/organizations/hooks/useOrganizationManagement';
import { PlaceSearch } from '../PlaceSearch';
import { createVenueFromPlaceSchema, type CreateVenueFromPlaceInput } from '../../schemas/venueSchemas';
import { useMediaUpload } from '@/hooks/useMediaUpload';
import { usePlaceDetails } from '../../hooks/useVenueManagement';
import type { Place } from '../../types';

interface CreateVenueFormProps {
  onSubmit: (values: CreateVenueFromPlaceInput) => Promise<void>;
  formId?: string;
  onResetComplete?: () => void;
}

export function CreateVenueForm({ 
  onSubmit, 
  formId = 'create-venue-form', 
  onResetComplete 
}: CreateVenueFormProps) {
  const [selectedPlace, setSelectedPlace] = useState<Place | null>(null);
  const [showPlaceSearch, setShowPlaceSearch] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const uploadMutation = useMediaUpload({ folder: 'venues' });

  // Fetch place details when a place is selected
  const { data: placeDetails, isLoading: isLoadingPlaceDetails } = usePlaceDetails(
    selectedPlace?.place_id || '',
    !!selectedPlace
  );

  // Validate required place details
  const placeValidation = useMemo(() => {
    if (!placeDetails || isLoadingPlaceDetails) {
      return { isValid: true, missingFields: [] as string[] }; // Still loading or no data yet
    }

    const requiredFields = {
      name: placeDetails.name,
      latitude: placeDetails.latitude,
      longitude: placeDetails.longitude,
      formatted_address: placeDetails.formatted_address,
      primary_type: placeDetails.primary_type,
      rating: placeDetails.rating,
      user_rating_count: placeDetails.user_rating_count,
    };

    const missingFields: string[] = [];
    Object.entries(requiredFields).forEach(([field, value]) => {
      if (value === undefined || value === null || value === '') {
        missingFields.push(field.replace(/_/g, ' '));
      }
    });

    return {
      isValid: missingFields.length === 0,
      missingFields,
    };
  }, [placeDetails, isLoadingPlaceDetails]);

  // Operating hours are available from placeDetails when API provides them

  // Fetch organizations dynamically
  const { data: orgData, isLoading: isLoadingOrgs, refetch: refetchOrgs } = useOrganizations();
  const createOrgMutation = useCreateOrganization();
  
  const organizations: ComboboxItem[] = [
    { value: 'create-new', label: '➕ Create new organization...' },
    ...(orgData?.organizations || []).map(org => ({
      value: org.id.toString(),
      label: org.name,
      venueCount: org.venue_count,
    }))
  ];

  const form = useForm({
    resolver: zodResolver(createVenueFromPlaceSchema),
    defaultValues: {
      place_id: '',
      organization_id: 0,
      media_url: '',
      operating_hours: {
        Sunday: { isOpen: false, from_hour: '', to_hour: '' },
        Monday: { isOpen: false, from_hour: '', to_hour: '' },
        Tuesday: { isOpen: false, from_hour: '', to_hour: '' },
        Wednesday: { isOpen: false, from_hour: '', to_hour: '' },
        Thursday: { isOpen: false, from_hour: '', to_hour: '' },
        Friday: { isOpen: false, from_hour: '', to_hour: '' },
        Saturday: { isOpen: false, from_hour: '', to_hour: '' },
      },
    },
  });

  const handlePlaceSelect = (place: Place) => {
    setSelectedPlace(place);
    form.setValue('place_id', place.place_id);
    setShowPlaceSearch(false);
  };

  const handleMediaUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const response = await uploadMutation.mutateAsync(file);
      form.setValue('media_url', response.media_url);
      return { media_url: response.media_url };
    } finally {
      setIsUploading(false);
    }
  };

  const handleCreateOrganization = async () => {
    if (!newOrgName.trim()) return;
    
    try {
      const result = await createOrgMutation.mutateAsync({ name: newOrgName });
      
      // Set the form value to the newly created organization
      form.setValue('organization_id', result.id, { shouldValidate: true });
      
      // Reset create form state
      setNewOrgName('');
      setShowCreateOrg(false);
      
      // Refetch organizations to include the new one
      await refetchOrgs();
    } catch (error) {
      // Error is handled by the mutation with toast
    }
  };

  const handleSubmit = async (values: CreateVenueFromPlaceInput) => {
    try {
      // Only include operating_hours if they were manually entered (no API data)
      const submitData = {
        ...values,
        operating_hours: !placeDetails?.operating_hours ? values.operating_hours : undefined,
      };
      await onSubmit(submitData);
      form.reset();
      setSelectedPlace(null);
      setShowPlaceSearch(true);
      setShowCreateOrg(false);
      setNewOrgName('');
      onResetComplete?.();
    } catch (error) {
      // Error is handled by the parent component
      // We just need to catch it to prevent unhandled promise rejection
    }
  };


  return (
    <Form {...form}>
      <form id={formId} onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        {/* Step 1: Search and Select Place */}
        <Card>
          <CardHeader>
            <CardTitle>Step 1: Find a Place</CardTitle>
            <CardDescription>
              Search for a place using Google Places
            </CardDescription>
          </CardHeader>
          <CardContent>
            <PlaceSearch 
              onPlaceSelect={handlePlaceSelect}
              selectedPlace={selectedPlace}
              showResults={showPlaceSearch}
              onToggleResults={() => setShowPlaceSearch(true)}
            />
          </CardContent>
        </Card>

        {/* Show loading state while fetching place details */}
        {selectedPlace && isLoadingPlaceDetails && (
          <Card>
            <CardContent className="py-8">
              <div className="flex items-center justify-center space-x-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                <span className="text-sm text-muted-foreground">Loading place details...</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Show validation error if place details are incomplete */}
        {selectedPlace && !isLoadingPlaceDetails && !placeValidation.isValid && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Incomplete Place Information</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>This place is missing required information and cannot be added as a venue.</p>
              <p className="font-medium">Missing fields:</p>
              <ul className="list-disc list-inside text-sm">
                {placeValidation.missingFields.map((field) => (
                  <li key={field}>{field}</li>
                ))}
              </ul>
              <p className="mt-3">
                Please contact the administrators about this issue or try selecting a different place.
              </p>
            </AlertDescription>
          </Alert>
        )}

        {/* Step 2: Operating Hours (only if not provided by API) */}
        {selectedPlace && !isLoadingPlaceDetails && !placeDetails?.operating_hours && placeValidation.isValid && (
          <Card>
            <CardHeader>
              <CardTitle>Step 2: Set Operating Hours</CardTitle>
              <CardDescription>
                Please specify the operating hours for each day
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {(['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const).map((day) => (
                  <div key={day} className="space-y-2 p-4 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">{day}</h4>
                      <FormField
                        control={form.control}
                        name={`operating_hours.${day}.isOpen`}
                        render={({ field }) => (
                          <FormItem className="flex items-center space-x-2">
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                            <FormLabel className="!mt-0">
                              {field.value ? 'OPEN' : 'CLOSED'}
                            </FormLabel>
                          </FormItem>
                        )}
                      />
                    </div>
                    
                    {form.watch(`operating_hours.${day}.isOpen`) && (
                      <div className="flex gap-4 items-end">
                        <FormField
                          control={form.control}
                          name={`operating_hours.${day}.from_hour`}
                          render={({ field }) => (
                            <FormItem className="flex-1">
                              <FormLabel>Opening Hour *</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  type="time"
                                  disabled={!form.watch(`operating_hours.${day}.isOpen`)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name={`operating_hours.${day}.to_hour`}
                          render={({ field }) => (
                            <FormItem className="flex-1">
                              <FormLabel>Closing Hour *</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  type="time"
                                  disabled={!form.watch(`operating_hours.${day}.isOpen`)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Organization Selection */}
        {selectedPlace && !isLoadingPlaceDetails && placeValidation.isValid && (
          <Card>
            <CardHeader>
              <CardTitle>Step {placeDetails?.operating_hours ? '2' : '3'}: Select Organization</CardTitle>
              <CardDescription>
                Associate this venue with an organization
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!showCreateOrg ? (
                <FormField
                  control={form.control}
                  name="organization_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Organization *</FormLabel>
                      <FormControl>
                        <ResponsiveCombobox
                          items={organizations}
                          value={field.value && field.value > 0 ? field.value.toString() : undefined}
                          onValueChange={(value) => {
                            if (value === 'create-new') {
                              setShowCreateOrg(true);
                            } else {
                              field.onChange(value ? parseInt(value) : 0);
                            }
                          }}
                          placeholder={isLoadingOrgs ? "Loading organizations..." : "Select organization"}
                          searchPlaceholder="Search organizations..."
                          disabled={isLoadingOrgs}
                          data-testid="venue-form-org-select"
                        />
                      </FormControl>
                      <FormDescription>
                        Select an existing organization or create a new one
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : (
                <div className="space-y-4">
                  <FormItem>
                    <FormLabel>New Organization Name *</FormLabel>
                    <FormControl>
                      <Input
                        value={newOrgName}
                        onChange={(e) => setNewOrgName(e.target.value)}
                        placeholder="e.g., Starbucks"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleCreateOrganization();
                          }
                        }}
                      />
                    </FormControl>
                    <FormDescription>
                      Enter the name of the new organization
                    </FormDescription>
                  </FormItem>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={handleCreateOrganization}
                      disabled={!newOrgName.trim() || createOrgMutation.isPending}
                    >
                      {createOrgMutation.isPending ? 'Creating...' : 'Create & Select'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowCreateOrg(false);
                        setNewOrgName('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 4: Media Upload */}
        {selectedPlace && !isLoadingPlaceDetails && placeValidation.isValid && form.watch('organization_id') > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Step {placeDetails?.operating_hours ? '3' : '4'}: Upload Venue Image</CardTitle>
              <CardDescription>
                Upload an image for this venue (required)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="media_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Venue Image *</FormLabel>
                    <FormControl>
                      <MediaUpload
                        value={field.value}
                        onChange={field.onChange}
                        onUpload={handleMediaUpload}
                        disabled={isUploading}
                      />
                    </FormControl>
                    <FormDescription>
                      Upload a high-quality image of the venue (JPEG or PNG, max 5MB)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>
        )}

      </form>
    </Form>
  );
}